import { type Map as MaplibreMap } from "maplibre-gl";
import type { Feature } from "geojson";

import { COLOR_LABEL } from "@/constants/colors";
import { UNIQUE_ID_PROPERTY } from "@/constants/map";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetDetailSchemaResponse,
  DatasetStyle,
  EColorLabel
} from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";

import { isOrdinalType } from "../dataset";
import { getRankOffset, getTextSizeExpression } from "../map";

/**
 * Ranks items highest-score-first using *dense* ranking: items sharing a score
 * share a rank, and the next distinct score takes the immediately following
 * number (90, 85, 85, 80 → 1, 2, 2, 3).
 *
 * `maxRank` caps the rank NUMBER, not the item count, so a tied group is never
 * split across the cutoff — every item ranked `#1`..`#maxRank` is returned even
 * when that is more than `maxRank` items. Scores are compared as raw numbers,
 * never as the truncated value shown on the label, so two features only tie
 * when their full decimal values match.
 */
export const assignDenseRanks = <T>(
  items: T[],
  getScore: (item: T) => number,
  maxRank: number
): Array<{ item: T; rank: number }> => {
  const sorted = [...items].sort((a, b) => getScore(b) - getScore(a));
  const ranked: Array<{ item: T; rank: number }> = [];

  let rank = 0;
  let previousScore: number | undefined;

  for (const item of sorted) {
    const score = getScore(item);
    if (score !== previousScore) {
      rank += 1;
      previousScore = score;
    }
    if (rank > maxRank) break;
    ranked.push({ item, rank });
  }

  return ranked;
};

export const getTopRankedFeatures = (
  features: GeoJSON.Feature[],
  attributeName: string,
  maxRanking: number
) => {
  const rankableFeatures = features.filter((f) => {
    const value = f.properties?.[attributeName];
    return typeof value === "number" && !Number.isNaN(value);
  });

  return assignDenseRanks(
    rankableFeatures,
    (f) => f.properties?.[attributeName] as number,
    maxRanking
  ).map(({ item, rank }) => ({
    ...item,
    properties: {
      ...item.properties,
      [UNIQUE_ID_PROPERTY]: item.properties?.[UNIQUE_ID_PROPERTY],
      rank: `#${rank}`
    }
  }));
};

export const mergeRankToFeatures = (
  isRankingLabelEnabled: boolean,
  features: Feature[],
  style?: DatasetStyle
) => {
  if (
    !style?.rankConfig?.isEnabled ||
    !style.labelConfig?.attribute_name ||
    !isRankingLabelEnabled
  ) {
    return features;
  }

  const maxRanking = style.rankConfig.maxRanking ?? 10;
  const attributeName = style.labelConfig.attribute_name;

  const rankedFeatures = getTopRankedFeatures(
    features,
    attributeName,
    maxRanking
  );

  const rankMap = new Map(
    rankedFeatures.map((f) => [
      f.properties?.[UNIQUE_ID_PROPERTY],
      f.properties?.rank
    ])
  );

  return features.map((f) => ({
    ...f,
    properties: {
      ...Object.fromEntries(
        Object.entries(f.properties ?? {}).filter(([k]) => k !== "rank")
      ),
      ...(rankMap.has(f.properties?.[UNIQUE_ID_PROPERTY])
        ? { rank: rankMap.get(f.properties?.[UNIQUE_ID_PROPERTY]) }
        : {})
    }
  }));
};

export const addRankLabelLayer = (
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataConfig: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  beforeId?: string
) => {
  if (!map || !dataConfig.sublayers || !isRankingLabelEnabled) return;

  const visibility =
    style.visibility === "visible" && style.rankConfig?.isEnabled
      ? "visible"
      : "none";

  const isPOI = dataConfig.type === "poi";
  const isSiteProfiling = dataConfig.type === "site_profiling";

  const sourceId = map.getSource(`${dataConfig.id}-centroid`)
    ? `${dataConfig.id}-centroid`
    : dataConfig.id;

  map.addLayer(
    {
      id: dataConfig.sublayers.rank,
      type: "symbol",
      source: sourceId,
      layout: {
        "symbol-placement": "point",
        "text-field": ["to-string", ["get", "rank"]],
        "text-font": ["Open Sans Bold", "Arial Unicode MS Regular"],
        "text-size": getTextSizeExpression(isPOI),
        "text-anchor": isPOI ? "bottom" : "center",
        "text-offset": [
          0,
          getRankOffset(style.labelConfig.show_label, isPOI, isSiteProfiling)
        ],
        "text-allow-overlap": false,
        "text-pitch-alignment": "viewport",
        "text-rotation-alignment": "viewport",
        visibility
      },
      paint: {
        "text-color":
          COLOR_LABEL[style.labelConfig?.color_scale || EColorLabel.BLACK_WHITE]
            .COLOR,
        "text-halo-color":
          COLOR_LABEL[style.labelConfig?.color_scale || EColorLabel.BLACK_WHITE]
            .HALO_COLOR,
        "text-halo-width": 1.2,
        "text-opacity": 1
      }
    },
    beforeId
  );
};

export const isRankableAttribute = (
  schema: DatasetDetailSchemaResponse[] | DatasetAttributeSchema[],
  selectedAttributeName: string
) => {
  // Match by `attribute_name`, not `id`: the caller's id comes from
  // `data.categories` (positional ids "0", "1", …), while `schema` is often
  // `data.data.schema` (real backend ids) — so an id lookup silently misses and
  // `isOrdinalType(undefined)` wrongly reports every attribute as rankable.
  // `attribute_name` is stable across both sources.
  const dataType = schema?.find(
    (value) => value.attribute_name === selectedAttributeName
  )?.data_type as DataSchemaType; // TODO will remove assertion when type for data_type updated

  return !isOrdinalType(dataType);
};
